import { isNil } from "lodash";
import { DateRange } from "moment-range";
import moment from "moment-timezone";
import type * as Moment from "moment-timezone";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";
import { ChannelCategory } from "src/types/channel";
import { PushTriggerModeEnum } from "src/types/enum/PushTriggerModeEnum";
import { OfferTypeEnum } from "src/types/offer";
import { ThirdServiceCategory } from "src/types/posCredential";
import { BID_AUTO_VALUE } from "src/types/sponsored-ads";
import { maxNegativeTimezone } from "src/utils/dateUtils";
import {
    FORMAT_DATE,
    FORMAT_DATE_STD,
    FORMAT_DATE_WITH_TIME,
    PRICE_FACTOR,
} from "src/utils/formatters";
import { formatSlotToDuration, formatSlotToTime } from "src/utils/hourSlots";
import { MarketingActionVariantEnum } from "src/utils/variants";
import {
    MAX_EDGE_VALUE,
    MIN_EDGE_VALUE,
} from "src/views/marketing-actions/components/form/ScenarioCard/AddScenarioDialog";
import { detectMissingDaysInStartAndEndComparePeriod } from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.utils";
import { v4 as uuid } from "uuid";
import * as Yup from "yup";
import {
    RecurrenceFrequence,
    isAllowCategorySelectionByServiceAndOfferType,
} from "./MarketingActionCreateView.logic";

function buildDate(
    date: string,
    slot: number,
    translateCommon: TFunction,
    tz?: string
): moment.Moment {
    const time = formatSlotToTime(slot >= 24 ? slot - 24 : slot, translateCommon);
    const formattedDate = moment(date).format(FORMAT_DATE);

    const currentDate = tz
        ? moment
              .tz(`${formattedDate} ${time}`, FORMAT_DATE_WITH_TIME, tz)
              .add(slot >= 24 ? 1 : 0, "day")
        : moment(`${formattedDate} ${time}`, FORMAT_DATE_WITH_TIME).add(slot >= 24 ? 1 : 0, "day");

    return currentDate;
}

function isSpecialTimeRange(currentValue, specialStartTime, specialEndTime): boolean {
    return currentValue >= specialStartTime && currentValue <= specialEndTime;
}

function isSpecialDateRange(
    currentValue: Moment.Moment,
    specialStartDate: string,
    specialEndDate: string,
    specialStartTime: number,
    specialEndTime: number
): boolean {
    if (specialStartTime && specialEndTime) {
        const startDateRange = moment(
            moment(specialStartDate).add(formatSlotToDuration(specialStartTime), "milliseconds")
        );
        const endDateRange = moment(
            moment(specialEndDate).add(formatSlotToDuration(specialEndTime), "milliseconds")
        );

        return currentValue.isBetween(startDateRange, endDateRange, undefined, "[]");
    }

    const startDateRange = moment(moment(specialStartDate).format(FORMAT_DATE_STD));
    const endDateRange = moment(moment(specialEndDate).format(FORMAT_DATE_STD));

    return currentValue.startOf("day").isBetween(startDateRange, endDateRange, undefined, "[]");
}

export const detectScenarioOverlap = (
    scenarios: any[],
    min: number,
    max: number,
    updateId: number
): boolean => {
    if (scenarios === undefined || scenarios.length === 0) {
        return false;
    }
    for (const scenario of scenarios) {
        if (scenario.id === updateId) {
            continue;
        }
        if (min > scenario.triggerMin && min < scenario.triggerMax) {
            return true;
        }
        if (max > scenario.triggerMin && max < scenario.triggerMax) {
            return true;
        }
        if (min <= scenario.triggerMin && max >= scenario.triggerMax) {
            return true;
        }
    }
    return false;
};

export const generateUpdatedEdgesScenariosList = (scenarios, updateScenario, isUpdate) => {
    const { triggerMax, triggerMin } = updateScenario;

    const eraseScenarioList = [];
    const updateScenarioList = [];

    scenarios.forEach((scenario) => {
        const { triggerMax: sMax, triggerMin: sMin } = scenario;

        // utilise pour ne pas rajouter le scénario dans la liste pendant une update
        if (isUpdate === true && scenario.id === updateScenario.id) {
            return;
        }

        // complètement englobé => on supprime le scénario
        if (triggerMax >= sMax && triggerMin <= sMin) {
            eraseScenarioList.push(scenario);
            return;
        }

        // englobe partiellement a gauche => on update le trigger Max
        if (triggerMin > sMin && triggerMax >= sMax && sMax > triggerMin) {
            updateScenarioList.push({
                ...scenario,
                triggerMax: triggerMin,
                uuid: uuid(),
            });
            return;
        }

        // englobe partiellement a droite => on update le trigger Min
        if (triggerMin <= sMin && triggerMax < sMax && sMin < triggerMax) {
            updateScenarioList.push({
                ...scenario,
                triggerMin: triggerMax,
                uuid: uuid(),
            });
            return;
        }

        // englobé par le scénario deja présent => il faut split le scénario deja présent en 2 pour y insérer le nouveau au milieu
        if (triggerMin > sMin && triggerMax < sMax) {
            eraseScenarioList.push(scenario);
            updateScenarioList.push({
                ...scenario,
                triggerMax: triggerMin,
                uuid: uuid(),
            });
            updateScenarioList.push({
                ...scenario,
                triggerMin: triggerMax,
                uuid: uuid(),
            });
            return;
        }

        // on push tous les autres scénarios qui ne sont pas impactés sans les modifier
        updateScenarioList.push(scenario);
    });

    return {
        eraseScenarioList,
        updateScenarioList,
    };
};

export const YupSchemaScenarioBuilder = ({
    t,
    variants,
    source,
}: {
    t: TFunction;
    variants: MarketingActionVariantEnum[];
    source: string;
}): Yup.ObjectSchema => {
    const scenarioSchemaValidator = {
        is: () => variants.includes(MarketingActionVariantEnum.SmartScheduler),
        // biome-ignore lint/suspicious/noThenProperty: expected
        then: Yup.mixed().required(),
    };

    return Yup.object()
        .shape({
            scenarioTemp: Yup.object({
                triggerMin: Yup.number()
                    .required()
                    .test({
                        name: "triggerMin",
                        message: t("validation.triggerMin"),
                        test: function (value) {
                            const { triggerMax } = this.parent;

                            if (value >= triggerMax) {
                                return this.createError({
                                    path: "scenarioTemp.triggerMin",
                                    message: t("edges-error-min-max"),
                                });
                            }

                            if (value < MIN_EDGE_VALUE) {
                                return this.createError({
                                    path: "scenarioTemp.triggerMin",
                                    message: t("edges-error-min-too-lower", {
                                        value: MIN_EDGE_VALUE,
                                    }),
                                });
                            }

                            return true;
                        },
                    }),
                triggerMax: Yup.number()
                    .required()
                    .test({
                        name: "triggerMax",
                        message: t("validation.triggerMax"),
                        test: function (value) {
                            const { triggerMin } = this.parent;

                            if (!(value > triggerMin)) {
                                return this.createError({
                                    path: "scenarioTemp.triggerMax",
                                    message: t("edges-error-max-min"),
                                });
                            }

                            if (value > MAX_EDGE_VALUE) {
                                return this.createError({
                                    path: "scenarioTemp.triggerMax",
                                    message: t("edges-error-max-too-higher", {
                                        value: MAX_EDGE_VALUE,
                                    }),
                                });
                            }

                            return true;
                        },
                    }),
                value: Yup.mixed().label(t("offer-type")).when("channel", scenarioSchemaValidator),
                discount: Yup.mixed().when("value", {
                    is: function (v) {
                        return (
                            [
                                OfferTypeEnum.DiscountByItem,
                                OfferTypeEnum.FreeDelivery,
                                OfferTypeEnum.FlatDelivery,
                            ].includes(v) ||
                            (v === OfferTypeEnum.Bogo && source === ThirdServiceCategory.Ubereats)
                        );
                    },
                    otherwise: Yup.mixed()
                        .label(t("discount"))
                        .when("channel", scenarioSchemaValidator),
                }),
                cart: Yup.mixed()
                    .test({
                        name: "cart",
                        message: t("validation.cart"),
                        test: function (value) {
                            const type = this.parent.value;
                            const service = source;
                            if (
                                [
                                    ThirdServiceCategory.Deliveroo,
                                    ThirdServiceCategory.JustEat,
                                ].includes(service as ThirdServiceCategory) &&
                                type === OfferTypeEnum.FreeDelivery &&
                                isNil(value)
                            ) {
                                return this.createError({
                                    path: "scenarioTemp.cart",
                                    message: t("form.valid.required", {
                                        field: t("form.fields.the-min-basket"),
                                    }),
                                });
                            }

                            return true;
                        },
                    })
                    .when("value", {
                        is: function (v) {
                            return [
                                OfferTypeEnum.DiscountByItem,
                                OfferTypeEnum.FreeDelivery,
                                OfferTypeEnum.Bogo,
                            ].includes(v);
                        },
                        otherwise: Yup.mixed()
                            .label(t("cart"))
                            .when("channel", scenarioSchemaValidator),
                    }),
                // Gestion d'erreur des produits en bas du yup car nécessite la récupération d'une valeur trop haute dans l'arborescence
                audience: Yup.number(),
            }),
        })
        .test({
            test: function (values) {
                const service = source as ThirdServiceCategory;
                const offerType = values?.scenarioTemp?.value;
                if (
                    [ThirdServiceCategory.Deliveroo, ThirdServiceCategory.JustEat].includes(
                        service
                    ) &&
                    offerType === OfferTypeEnum.Bogo
                ) {
                    if (!values?.scenarioTemp?.discount) {
                        return this.createError({
                            path: "scenarioTemp.discount",
                            message: t("form.valid.required-f", {
                                field: t("form.fields.the-discount"),
                            }),
                        });
                    }

                    if (isNil(values.scenarioTemp?.cart)) {
                        return this.createError({
                            path: "scenarioTemp.cart",
                            message: t("form.valid.required", {
                                field: t("form.fields.the-min-basket"),
                            }),
                        });
                    }
                }

                const allowCategorySelection = isAllowCategorySelectionByServiceAndOfferType(
                    service,
                    offerType
                );
                if (allowCategorySelection === false) {
                    const checkProductListError = checkProductsList(
                        offerType,
                        values?.scenarioTemp?.products,
                        "scenarioTemp.products",
                        t
                    );
                    if (checkProductListError) {
                        return this.createError(checkProductListError);
                    }
                } else {
                    const checkCategoryItemListError = checkCategoryItemList(
                        values?.scenarioTemp?.categoryItemList,
                        "scenarioTemp.categoryItemList",
                        t
                    );

                    if (checkCategoryItemListError) {
                        return this.createError(checkCategoryItemListError);
                    }
                }
            },
        });
};

const ProductErrorGenerator = ({
    createError,
    value,
    product,
    pathProduct,
    pathCategoryItem,
    categoryItemList,
    t,
    allowCategorySelection,
}) => {
    if (allowCategorySelection === false) {
        const checkProductListError = checkProductsList(value, product, pathProduct, t);
        if (checkProductListError) {
            return createError(checkProductListError);
        }
    } else {
        const checkCategoryItemListError = checkCategoryItemList(
            categoryItemList,
            pathCategoryItem,
            t
        );
        if (checkCategoryItemListError) {
            return createError(checkCategoryItemListError);
        }
    }
    return null;
};
// Ne devrait pas être en Pascal case
export const YupSchemaBuilder = ({
    t,
    translateCommon,
    variants,
}: {
    t: TFunction;
    translateCommon: TFunction;
    variants: MarketingActionVariantEnum[];
}): Yup.ObjectSchema => {
    const offerSchemaValidator = {
        is: () =>
            variants.includes(MarketingActionVariantEnum.Offer) ||
            variants.includes(MarketingActionVariantEnum.Duo),
        // biome-ignore lint/suspicious/noThenProperty: expected
        then: Yup.mixed().required(),
    };

    const sponsoredAdValidator = {
        is: () =>
            variants.includes(MarketingActionVariantEnum.SponsoredAd) ||
            variants.includes(MarketingActionVariantEnum.Duo),
        // biome-ignore lint/suspicious/noThenProperty: expected
        then: Yup.mixed().required(),
    };

    const scenarioBasicConfigValueValidator = {
        channelIds: Yup.array().min(1, t(`validation.reference-channel`)),
        scenarioSpecificActivation: Yup.object({
            firstSlotScenarioEnabled: Yup.boolean().required(),
            firstSlotScenarioUuid: Yup.mixed().when("firstSlotScenarioEnabled", {
                is: (v) => v === true,
                // biome-ignore lint/suspicious/noThenProperty: expected
                then: Yup.string().nullable().required(t("validation.specific-cases")),
                otherwise: Yup.string().nullable(),
            }),
            peakScenarioEnabled: Yup.boolean().required(),
            peakScenarioUuid: Yup.mixed().when("peakScenarioEnabled", {
                is: (v) => v === true,
                // biome-ignore lint/suspicious/noThenProperty: expected
                then: Yup.string().nullable().required(t("validation.specific-cases")),
                otherwise: Yup.string().nullable(),
            }),
        }),
    };

    const offerCharacteristicValueValidator = {
        value: Yup.mixed()
            .label(t("offer-type"))
            .when("channel", offerSchemaValidator)
            .test({
                name: "offer-type",
                test: function () {
                    if (this.parent.isCheeckyTuesday === true) {
                        return false;
                    }
                    return true;
                },
            }),
        discount: Yup.mixed().when("value", {
            is: function (v) {
                return [
                    OfferTypeEnum.DiscountByItem,
                    OfferTypeEnum.FreeDelivery,
                    OfferTypeEnum.Bogo,
                ].includes(v);
            },
            otherwise: Yup.mixed().label(t("discount")).when("channel", offerSchemaValidator),
        }),
        cart: Yup.mixed().when("value", {
            is: function (v) {
                return [
                    OfferTypeEnum.DiscountByItem,
                    OfferTypeEnum.FreeDelivery,
                    OfferTypeEnum.Bogo,
                ].includes(v);
            },
            otherwise: Yup.mixed().label(t("cart")).when("channel", offerSchemaValidator),
        }),
        // Gestion d'erreur des produits en bas du yup car nécessite la récupération d'une valeur trop haute dans l'arborescence
    };

    return Yup.object()
        .shape({
            posList: Yup.array()
                .min(1)
                .max(100, t("validation.max-pos-selected", { max: 100 })),
            condition: Yup.object({})
                .test({
                    name: "trigger-start-value",
                    test: function (value) {
                        if (!variants.includes(MarketingActionVariantEnum.AutoRule)) {
                            return true;
                        }

                        if (value?.triggerStart?.metaData?.channelIds?.length === 0) {
                            return this.createError({
                                path: "condition.triggerStart.metaData.channelIds",
                                message: t(`validation.reference-channel`),
                            });
                        }

                        return true;
                    },
                })
                .test({
                    name: "trigger-start-metric",
                    test: function (value) {
                        if (
                            variants.includes(MarketingActionVariantEnum.AutoRule) &&
                            (value?.triggerStart?.metric === "" ||
                                value?.triggerStart?.metric === undefined ||
                                value?.triggerStart?.metric === null)
                        ) {
                            return this.createError({
                                path: "condition.triggerStart.metric",
                                message: t(`validation.auto-rule.missing-trigger-value`),
                            });
                        }
                        return true;
                    },
                })
                .test({
                    name: "trigger-stop-value",
                    test: function (value) {
                        if (
                            variants.includes(MarketingActionVariantEnum.AutoRule) &&
                            value?.triggerStopStatus === "enable" &&
                            (value?.triggerStop?.metaData?.value < 0 ||
                                isNaN(value?.triggerStop?.metaData?.value))
                        ) {
                            return this.createError({
                                path: "condition.triggerStop.metaData.value",
                                message: t(`validation.auto-rule.bad-value`),
                            });
                        }
                        return true;
                    },
                }),
            serviceAndChannel: Yup.object().shape({
                channel: Yup.object().shape({
                    value: Yup.mixed()
                        .label(t("mode"))
                        .when("channel", offerSchemaValidator)
                        .test("channel", null, function (value) {
                            if (this.parent.isSpecialOperation && value === "takeAway") {
                                return this.createError({
                                    message: t("validation.delivery-not-allowed-special-offer"),
                                });
                            } else {
                                return true;
                            }
                        }),
                }),
                service: Yup.object().shape({
                    value: Yup.mixed().label(t("service")).when("service", sponsoredAdValidator),
                }),
            }),
            sponsoredAdCharacteristic: Yup.object({
                bid: Yup.object({
                    value: Yup.mixed()
                        .label(t("bid"))
                        .when("service", sponsoredAdValidator)
                        .test({
                            name: "bid-interval",
                            test: function (value) {
                                if (
                                    variants.includes(MarketingActionVariantEnum.Offer) &&
                                    !variants.includes(MarketingActionVariantEnum.Duo)
                                ) {
                                    return true;
                                }
                                if (value === BID_AUTO_VALUE) {
                                    return true;
                                } else if (!value) {
                                    return this.createError({
                                        message: t("validation.bid.missing"),
                                    });
                                } else if (
                                    this.parent.min &&
                                    this.parent.max &&
                                    (value < this.parent.min || value > this.parent.max)
                                ) {
                                    return this.createError({
                                        message: t("validation.bid.out-of-range", {
                                            min: this.parent.min / PRICE_FACTOR,
                                            max: this.parent.max / PRICE_FACTOR,
                                        }),
                                    });
                                } else {
                                    return true;
                                }
                            },
                        }),
                    min: Yup.number().optional(),
                    max: Yup.number().optional(),
                }),
                slotBudget: Yup.object({
                    value: Yup.mixed()
                        .label(t("budget"))
                        .when("service", sponsoredAdValidator)
                        .test({
                            name: "bid-value",
                            test: function (value) {
                                if (value && (value < this.parent.min || value > this.parent.max)) {
                                    return this.createError({
                                        message: t("validation.budget.interval", {
                                            min: this.parent.min,
                                            max: this.parent.max,
                                        }),
                                    });
                                }
                                return true;
                            },
                        }),
                }),
                weeklyBudget: Yup.object({
                    value: Yup.mixed()
                        .label(t("budget"))
                        .when("service", sponsoredAdValidator)
                        .test({
                            name: "bid-value-weekly",
                            test: function (value) {
                                if (value < this.parent.min || value > this.parent.max) {
                                    return this.createError({
                                        message: t("validation.budget.interval", {
                                            min: this.parent.min,
                                            max: this.parent.max,
                                        }),
                                    });
                                }
                                return true;
                            },
                        }),
                }),
                totalBudget: Yup.object({
                    value: Yup.mixed()
                        .label(t("budget"))
                        .when("service", sponsoredAdValidator)
                        .test({
                            name: "bid-value-total",
                            test: function (value) {
                                if (value < this.parent.min || value > this.parent.max) {
                                    return this.createError({
                                        message: t("validation.budget.interval", {
                                            min: this.parent.min,
                                            max: this.parent.max,
                                        }),
                                    });
                                }
                                return true;
                            },
                        }),
                }),
            }),
            scenarios:
                variants.includes(MarketingActionVariantEnum.SmartScheduler) === true
                    ? Yup.array().of(Yup.object(offerCharacteristicValueValidator))
                    : undefined,
            offerCharacteristic:
                variants.includes(MarketingActionVariantEnum.SmartScheduler) === false
                    ? Yup.object(offerCharacteristicValueValidator)
                    : undefined,
            scenarioBasicConfig:
                variants.includes(MarketingActionVariantEnum.SmartScheduler) === true
                    ? Yup.object(scenarioBasicConfigValueValidator)
                    : undefined,
            audience: Yup.object({
                value: Yup.mixed().test({
                    name: "noAudience",
                    test: function (value) {
                        if (variants.includes(MarketingActionVariantEnum.SmartScheduler) === true) {
                            return true;
                        }
                        if (!value) {
                            return this.createError({ message: t("validation.empty-audience") });
                        }
                        return true;
                    },
                }),
            }),
            recurrence: Yup.object({
                possTimezone: Yup.array(),
                startDate: Yup.date()
                    .typeError(t("validation.empty-start-date"))
                    .test({
                        name: "date",
                        test: function (value) {
                            let maxTimezone = null;
                            if (this.parent.possTimezone && this.parent.possTimezone.length > 0) {
                                maxTimezone = maxNegativeTimezone(this.parent.possTimezone);
                            }

                            const currentDate = buildDate(
                                this.parent.startDate,
                                this.parent.startTime,
                                (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                maxTimezone
                            );

                            const now = maxTimezone ? moment.tz(maxTimezone) : moment();
                            let startDate: Moment.Moment = this.parent.startDate;

                            if (maxTimezone) {
                                startDate = moment.tz(startDate, maxTimezone);
                            } else {
                                startDate = moment(startDate);
                            }

                            if (!value) {
                                return this.createError({
                                    message: t("validation.empty-start-date"),
                                });
                            } else if (
                                this.parent.isSpecialOperation &&
                                currentDate.isBefore(now) &&
                                this.parent.frequence === RecurrenceFrequence.ONETIME
                            ) {
                                return this.createError({
                                    message: t("validation.startDate-endDate.before-now"),
                                });
                            } else if (
                                this.parent.isSpecialOperation &&
                                this.parent.specialStartDate &&
                                !isSpecialDateRange(
                                    this.parent.specialStartTime ? currentDate : startDate,
                                    this.parent.specialStartDate,
                                    this.parent.specialEndDate,
                                    this.parent.specialStartTime,
                                    this.parent.specialEndTime
                                )
                            ) {
                                return this.createError({
                                    message: t("validation.specialStartDate-not-in-range"),
                                });
                            } else if (
                                value &&
                                this.parent.startTime &&
                                currentDate.isBefore(now, "day")
                            ) {
                                return this.createError({
                                    message: t("validation.startDate.after-now"),
                                });
                            }

                            return true;
                        },
                    }),

                startTime: Yup.string()
                    .nullable()
                    .test({
                        name: "date",
                        test: function (value) {
                            let maxTimezone = null;
                            if (this.parent.possTimezone && this.parent.possTimezone.length > 0) {
                                maxTimezone = maxNegativeTimezone(this.parent.possTimezone);
                            }

                            const currentDate = buildDate(
                                this.parent.startDate,
                                this.parent.startTime,
                                (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                maxTimezone
                            );
                            const now = maxTimezone ? moment.tz(maxTimezone) : moment();

                            if (!value) {
                                return this.createError({
                                    message: t("validation.empty-start-time"),
                                });
                            }

                            if (
                                this.parent.isSpecialOperation &&
                                this.parent.specialStartTime &&
                                this.parent.specialEndTime &&
                                !isSpecialTimeRange(
                                    value,
                                    this.parent.specialStartTime,
                                    this.parent.specialEndTime
                                )
                            ) {
                                return this.createError({
                                    message: t("validation.specialStartTime-not-in-range"),
                                });
                            }
                            if (
                                this.parent.isSpecialOperation &&
                                currentDate.isBefore(now) &&
                                this.parent.frequence === RecurrenceFrequence.ONETIME
                            ) {
                                return this.createError({
                                    message: t("validation.startDate-endDate.before-now"),
                                });
                            }
                            if ([RecurrenceFrequence.ONETIME].includes(this.parent.frequence)) {
                                if (value) {
                                    return currentDate.isBefore(now)
                                        ? this.createError({
                                              message: t("validation.startTime.after-now"),
                                          })
                                        : true;
                                } else {
                                    return false;
                                }
                            } else {
                                return true;
                            }
                        },
                    }),
                endTime: Yup.string()
                    .nullable()
                    .test("date", t("validation.endTime.after-start-date"), function (value) {
                        if (!value) {
                            return this.createError({ message: t("validation.empty-end-time") });
                        } else if (value) {
                            let maxTimezone = null;
                            if (this.parent.possTimezone && this.parent.possTimezone.length > 0) {
                                maxTimezone = maxNegativeTimezone(this.parent.possTimezone);
                            }

                            const currentStartDate = buildDate(
                                undefined,
                                this.parent.startTime,
                                (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                maxTimezone
                            );
                            const currentEndDate = buildDate(
                                undefined,
                                value,
                                (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                maxTimezone
                            );

                            const now = maxTimezone ? moment.tz(maxTimezone) : moment();

                            const currentSpecialEndDate = buildDate(
                                this.parent?.specialEndDate,
                                value,
                                (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                maxTimezone
                            );
                            const timeDifferenceInMinutes = new DateRange(
                                currentStartDate,
                                currentEndDate
                            ).diff("minutes");

                            if (timeDifferenceInMinutes < 15 && timeDifferenceInMinutes >= 0) {
                                return this.createError({
                                    message: t("validation.fifteen-minutes-slot"),
                                });
                            }

                            if (
                                (this.options.context as any)?.serviceAndChannel?.service.value ===
                                    ThirdServiceCategory.Glovo &&
                                timeDifferenceInMinutes < 60 &&
                                timeDifferenceInMinutes >= 0
                            ) {
                                return this.createError({
                                    message: t("validation.sixteen-minutes-slot"),
                                });
                            }

                            if (
                                this.parent.isSpecialOperation &&
                                this.parent.specialStartTime &&
                                this.parent.specialEndTime &&
                                !isSpecialTimeRange(
                                    value,
                                    this.parent.specialStartTime,
                                    this.parent.specialEndTime
                                )
                            ) {
                                return this.createError({
                                    message: t("validation.specialEndTime-not-in-range"),
                                });
                            }

                            if (
                                this.parent.isSpecialOperation &&
                                currentSpecialEndDate.isBefore(now) &&
                                this.parent.frequence === RecurrenceFrequence.ONETIME
                            ) {
                                return this.createError({
                                    message: t("validation.startDate-endDate.before-now"),
                                });
                            }

                            return currentEndDate.isAfter(currentStartDate);
                        } else {
                            return false;
                        }
                    }),
                endDate: Yup.date()
                    .nullable()
                    .when("ending", {
                        is: "forever",
                        // biome-ignore lint/suspicious/noThenProperty: expected
                        then: Yup.date().notRequired(),
                        otherwise: Yup.date().test({
                            name: "date",
                            test: function (value) {
                                let maxTimezone = null;
                                if (
                                    this.parent.possTimezone &&
                                    this.parent.possTimezone.length > 0
                                ) {
                                    maxTimezone = maxNegativeTimezone(this.parent.possTimezone);
                                }

                                const now = maxTimezone ? moment.tz(maxTimezone) : moment();

                                const currentSpecialEndDate = buildDate(
                                    this.parent?.specialEndDate,
                                    this.parent.endTime,
                                    (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                    maxTimezone
                                );
                                const currentEndDate = buildDate(
                                    this.parent.endDate,
                                    this.parent.endTime,
                                    (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                    maxTimezone
                                );

                                const currentStartDate = buildDate(
                                    this.parent.startDate,
                                    this.parent.startTime,
                                    (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                    maxTimezone
                                );

                                if (maxTimezone) {
                                    this.parent.endDate = moment.tz(
                                        this.parent.endDate,
                                        maxTimezone
                                    );
                                } else {
                                    this.parent.endDate = moment(this.parent.endDate);
                                }

                                if (
                                    !value &&
                                    ![RecurrenceFrequence.ONETIME].includes(this.parent.frequence)
                                ) {
                                    return this.createError({
                                        message: t("validation.empty-end-date"),
                                    });
                                } else if (
                                    this.parent.isSpecialOperation &&
                                    currentSpecialEndDate.isBefore(now) &&
                                    this.parent.frequence === RecurrenceFrequence.ONETIME
                                ) {
                                    return this.createError({
                                        message: t("validation.startDate-endDate.before-now"),
                                    });
                                } else if (
                                    this.parent.isSpecialOperation &&
                                    [RecurrenceFrequence.ONETIME].includes(this.parent.frequence) &&
                                    !isSpecialDateRange(
                                        currentStartDate,
                                        this.parent.specialStartDate,
                                        this.parent.specialEndDate,
                                        this.parent.specialStartTime,
                                        this.parent.specialEndTime
                                    )
                                ) {
                                    return this.createError({
                                        message: t("validation.specialEndDate-not-in-range"),
                                    });
                                } else if (
                                    this.parent.isSpecialOperation &&
                                    ![RecurrenceFrequence.ONETIME].includes(
                                        this.parent.frequence
                                    ) &&
                                    !isSpecialDateRange(
                                        this.parent.specialEndTime
                                            ? currentEndDate
                                            : this.parent.endDate,
                                        this.parent.specialStartDate,
                                        this.parent.specialEndDate,
                                        this.parent.specialStartTime,
                                        this.parent.specialEndTime
                                    )
                                ) {
                                    return this.createError({
                                        message: t("validation.specialEndDate-not-in-range"),
                                    });
                                } else if (value) {
                                    const currentDate = buildDate(
                                        this.parent.startDate,
                                        this.parent.startTime,
                                        (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                        maxTimezone
                                    );
                                    const endDate = buildDate(
                                        value,
                                        this.parent.endTime,
                                        (string) => t(`${COMMON_TRANSLATION_NAMESPACE}:${string}`),
                                        maxTimezone
                                    );
                                    return endDate.isAfter(currentDate)
                                        ? true
                                        : this.createError({
                                              message: t("form.valid.dates-equal-or-after"),
                                          });
                                } else {
                                    return true;
                                }
                            },
                        }),
                    }),
                days: Yup.array()
                    .nullable()
                    .when("frequence", {
                        is: RecurrenceFrequence.SPE,
                        // biome-ignore lint/suspicious/noThenProperty: expected
                        then: Yup.array()
                            .min(1, t("validation.min-one-day"))
                            .test("date", t("validation.including-date"), function (value) {
                                if (!value) {
                                    this.createError({
                                        message: t("form.valid.not-empty-customized"),
                                    });
                                } else if (value) {
                                    const startDate = this.parent.startDate;
                                    const endDate = this.parent.endDate;
                                    if (
                                        (startDate && value.includes(moment(startDate).day())) ||
                                        !endDate
                                    ) {
                                        return true;
                                    } else {
                                        this.createError({
                                            message: t("validation.including-date"),
                                        });
                                    }
                                } else {
                                    this.createError({ message: t("validation.including-date") });
                                }
                            }),
                    }),
            }),
            pushTriggerMode: Yup.string()
                .nullable()

                /**
                 *  TODO: I don´t know how to check if parallel is enabled for the source and service
                 *  using useParallelActionsEnabled2 hook
                 *
                 */
                // .test({
                //     name: "push-trigger-mode-required",
                //     test: function (value) {
                //         if (!variants.includes(MarketingActionVariantEnum.AutoRule)) {
                //             return true;
                //         }
                //         if (!value) {
                //             return this.createError({ message: t("validation.push-trigger-mode-required") });
                //         }
                //         return true;
                //     },
                // })
                .oneOf(
                    [PushTriggerModeEnum.NON_PARALLEL, PushTriggerModeEnum.PARALLEL],
                    t("validation.push-trigger-mode-invalid")
                ),
        })
        .test({
            test: function (values) {
                if (
                    variants.includes(MarketingActionVariantEnum.SmartScheduler) ||
                    variants.includes(MarketingActionVariantEnum.AutoRule)
                ) {
                    const dayOfStartDay = moment(values.recurrence?.startDate)?.day();
                    const days =
                        values.recurrence?.frequence === RecurrenceFrequence.ONETIME
                            ? [dayOfStartDay]
                            : values.recurrence?.days;

                    const condition = detectMissingDaysInStartAndEndComparePeriod({
                        startDateComparePeriod: values.scenarioBasicConfig.startDateComparePeriod,
                        endDateComparePeriod: values.scenarioBasicConfig.endDateComparePeriod,
                        days,
                    });

                    const formattedMissingInComparePeriodDayList = condition
                        .map((day) => {
                            return `${translateCommon("word-link.one")} ${translateCommon(
                                `enums.dows.${day}`
                            ).toLowerCase()}, `;
                        })
                        .join("");
                    if (formattedMissingInComparePeriodDayList.length > 0) {
                        return this.createError({
                            path: "scenarioBasicConfig.daysError",
                            message: formattedMissingInComparePeriodDayList,
                        });
                    }
                    return true;
                } else {
                    return true;
                }
            },
        })
        .test({
            test: function (values) {
                const checkBogoAndDeliverooError = checkBogoAndDeliveroo(values, t);
                if (checkBogoAndDeliverooError) {
                    return this.createError(checkBogoAndDeliverooError);
                }

                const checkSpecialOrDeliverooAndFreeDeliveryAndMandatoryMinBasketError =
                    checkSpecialOrDeliverooAndFreeDeliveryAndMandatoryMinBasket(
                        values,
                        variants,
                        t
                    );
                if (checkSpecialOrDeliverooAndFreeDeliveryAndMandatoryMinBasketError) {
                    return this.createError(
                        checkSpecialOrDeliverooAndFreeDeliveryAndMandatoryMinBasketError
                    );
                }

                const checkSpecialAndMandatoryDiscountAndNoDiscountError =
                    checkSpecialAndMandatoryDiscountAndNoDiscount(values, variants, t);
                if (checkSpecialAndMandatoryDiscountAndNoDiscountError) {
                    return this.createError(checkSpecialAndMandatoryDiscountAndNoDiscountError);
                }

                const allowCategorySelection = isAllowCategorySelectionByServiceAndOfferType(
                    values?.serviceAndChannel?.service?.value,
                    values?.offerCharacteristic?.value
                );

                if (variants.includes(MarketingActionVariantEnum.SmartScheduler)) {
                    const errors = [];
                    values.scenarios?.forEach((scenario, index) => {
                        const allowCategorySelection =
                            isAllowCategorySelectionByServiceAndOfferType(
                                values?.serviceAndChannel?.service?.value,
                                scenario.value
                            );
                        const result = ProductErrorGenerator({
                            createError: ({ path, message }: { path: any; message: any }) => {
                                return new Yup.ValidationError(message, null, path);
                            },
                            value: scenario.value,
                            product: scenario.products,
                            categoryItemList: scenario.categoryItemList,
                            pathProduct: `scenarios[${index}].value`,
                            pathCategoryItem: `scenarios[${index}].value`,
                            t,
                            allowCategorySelection,
                        });
                        if (result) {
                            errors.push(result);
                        }

                        // Empêcher d'avoir une livraison offerte en A emporter
                        if (
                            values?.serviceAndChannel?.channel?.value ===
                                ChannelCategory.TakeAway &&
                            [OfferTypeEnum.FreeDelivery, OfferTypeEnum.FlatDelivery].includes(
                                scenario.discount
                            )
                        ) {
                            const path = `scenarios[${index}].value`;
                            const message = "error";
                            const errorDeliveryTakeAway = new Yup.ValidationError(
                                message,
                                null,
                                path
                            );
                            errors.push(errorDeliveryTakeAway);
                        }
                    });
                    if (errors.length === 0) {
                        return true;
                    }

                    return new Yup.ValidationError(errors, null, "scenarios");
                } else {
                    return ProductErrorGenerator({
                        createError: this.createError,
                        value: values?.offerCharacteristic?.value,
                        product: values?.offerCharacteristic?.products,
                        categoryItemList: values?.offerCharacteristic?.categoryItemList,
                        pathProduct: "offerCharacteristic.products",
                        pathCategoryItem: "offerCharacteristic.categoryItemList",
                        t,
                        allowCategorySelection,
                    });
                }
            },
        });
};

const checkCategoryItemList = (categoryItemList, path, t) => {
    if (isNil(categoryItemList) || categoryItemList?.length === 0) {
        return {
            path,
            message: t("validation.discountByItem.products"),
        };
    }
    return null;
};

const checkProductsList = (offerType, productList, path, t) => {
    if (
        offerType === OfferTypeEnum.DiscountByItem &&
        (isNil(productList) ||
            productList.length === 0 ||
            productList.every((elm) => elm.discountOption) === false)
    ) {
        return {
            path,
            message: t("validation.discountByItem.products"),
        };
    } else if (
        (isNil(productList) || productList?.length === 0) &&
        [OfferTypeEnum.SpecificDish, OfferTypeEnum.FreeItem, OfferTypeEnum.Bogo].includes(offerType)
    ) {
        return {
            path,
            message: t("validation.otherDiscount.products"),
        };
    }
};

const checkSpecialAndMandatoryDiscountAndNoDiscount = (values, variants, t) => {
    if (
        variants.includes(MarketingActionVariantEnum.Special) &&
        values.offerCharacteristic.isMandatoryDiscount === true &&
        isNil(values.offerCharacteristic?.discount)
    ) {
        return {
            path: "offerCharacteristic.discount",
            message: t("form.valid.required-f", { field: t("form.fields.the-discount") }),
        };
    }
};

const checkSpecialOrDeliverooAndFreeDeliveryAndMandatoryMinBasket = (values, variants, t) => {
    if (
        (variants.includes(MarketingActionVariantEnum.Special) ||
            (values.serviceAndChannel?.service?.value === ThirdServiceCategory.Deliveroo &&
                values.offerCharacteristic?.value === OfferTypeEnum.FreeDelivery)) &&
        values.offerCharacteristic.isMandatoryMinBasket === true &&
        isNil(values.offerCharacteristic?.cart)
    ) {
        return {
            path: "offerCharacteristic.cart",
            message: t("form.valid.required", { field: t("form.fields.the-min-basket") }),
        };
    }
    return null;
};

const checkBogoAndDeliveroo = (values, t) => {
    if (
        [ThirdServiceCategory.Deliveroo, ThirdServiceCategory.JustEat].includes(
            values.serviceAndChannel?.service?.value
        ) &&
        values.offerCharacteristic?.value === OfferTypeEnum.Bogo
    ) {
        if (!values.offerCharacteristic?.discount) {
            return {
                path: "offerCharacteristic.discount",
                message: t("form.valid.required-f", { field: t("form.fields.the-discount") }),
            };
        }
        if (isNil(values.offerCharacteristic?.cart)) {
            return {
                path: "offerCharacteristic.cart",
                message: t("form.valid.required", { field: t("form.fields.the-min-basket") }),
            };
        }
    }
    return null;
};
